import React, { useState, useEffect } from 'react';
import { Cloud, HardDrive, Search, Trash2, Upload, User, Calendar, MapPin, ChevronRight } from 'lucide-react';
import { useAuthStore } from '@store/authStore';
import { useKundliStore } from '@store/kundliStore';
import { kundliService, KundliProfile } from '@services/kundliService';
import toast from 'react-hot-toast';
import { FREE_KUNDLI_TRANSLATIONS } from '../../../../languages/freeKundli';
import { formatDob, formatKundliDobForApi } from '@utils/dateUtils';

interface SavedKundliListProps {
    onSelect: (kundli: any | KundliProfile, isCloud: boolean) => void;
    onRefresh?: () => void;
    selectedId?: string | null;
    language: 'english' | 'hindi';
}

const SavedKundliList: React.FC<SavedKundliListProps> = ({ onSelect, onRefresh, selectedId, language }) => {
    const { isAuthenticated } = useAuthStore();
    const { savedKundlis, deleteKundli } = useKundliStore();
    const [cloudKundlis, setCloudKundlis] = useState<KundliProfile[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [uploadingId, setUploadingId] = useState<string | null>(null);

    const langKey = language === 'hindi' ? 'Hindi' : 'English';
    const t = FREE_KUNDLI_TRANSLATIONS[langKey];

    // Fetch cloud kundlis if logged in
    useEffect(() => {
        if (isAuthenticated) {
            fetchCloudKundlis();
        }
    }, [isAuthenticated]);

    const fetchCloudKundlis = async () => {
        try {
            setLoading(true);
            const response = await kundliService.getProfiles();
            setCloudKundlis(response.kundli_profiles || []);
        } catch (error) {
            console.error('Failed to fetch cloud kundlis:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteLocal = (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        deleteKundli(id);
        toast.success(t.kundliRemoved);
    };

    const handleDeleteCloud = async (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        try {
            await kundliService.deleteProfile(id);
            await fetchCloudKundlis();
            toast.success(t.kundliDeletedCloud);
        } catch (error) {
            toast.error('Failed to delete kundli');
        }
    };

    const handleUploadToCloud = async (localKundli: any, e: React.MouseEvent) => {
        e.stopPropagation();
        if (!isAuthenticated) {
            toast.error(t.loginToSave);
            return;
        }

        setUploadingId(localKundli.id);
        try {
            const apiDob = formatKundliDobForApi(localKundli.dob);
            if (!apiDob) {
                toast.error('Invalid date format');
                return;
            }

            await kundliService.createProfile({
                profile_name: localKundli.name,
                name: localKundli.name,
                dob: apiDob,
                birth_time: localKundli.birth_time,
                birth_location: localKundli.location,
                latitude: localKundli.latitude,
                longitude: localKundli.longitude,
                timezone: localKundli.timezone || '+5.5',
                gender: localKundli.gender
            });

            // Remove from local storage after successful upload
            deleteKundli(localKundli.id);
            await fetchCloudKundlis();
            toast.success(t.kundliSavedCloud);
        } catch (error) {
            toast.error('Failed to save to cloud');
        } finally {
            setUploadingId(null);
        }
    };

    // Filter kundlis based on search
    const filteredCloud = cloudKundlis.filter(k =>
        k.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    const filteredLocal = savedKundlis.filter(k =>
        k.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const formatDate = (dob: string) => {
        return formatDob(dob);
    };

    const getInitial = (name: string) => {
        return name?.charAt(0)?.toUpperCase() || '?';
    };

    const hasAnyKundlis = filteredCloud.length > 0 || filteredLocal.length > 0;

    return (
        <div className="bg-white/[0.08] border border-white/20 rounded-[24px] p-5 md:p-6 h-full">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <User size={18} className="text-primary-400" />
                {t.savedCharts}
            </h3>

            {/* Search */}
            <div className="relative mb-4">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-primary-400" />
                <input
                    type="text"
                    placeholder={t.searchCharts}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full bg-white/5 border border-white/20 rounded-xl py-2.5 pl-10 pr-4 text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/50 placeholder:text-white/60"
                />
            </div>

            {/* Kundli List */}
            <div className="space-y-2 max-h-[400px] overflow-y-auto custom-scrollbar">
                {loading && (
                    <div className="text-center py-8 text-primary-200/50">
                        <div className="w-6 h-6 border-2 border-primary-400/30 border-t-primary-400 rounded-full animate-spin mx-auto mb-2" />
                        {t.loading}
                    </div>
                )}

                {!loading && !hasAnyKundlis && (
                    <div className="text-center py-8 text-primary-200/50 text-sm">
                        <User size={32} className="mx-auto mb-3 opacity-30" />
                        {t.noSavedKundlisYet}
                    </div>
                )}

                {/* Cloud Kundlis */}
                {filteredCloud.length > 0 && (
                    <>
                        <div className="flex items-center gap-2 text-xs text-primary-300/60 font-medium uppercase tracking-wider py-2">
                            <Cloud size={12} />
                            {t.cloudSaved}
                        </div>
                        
                            {filteredCloud.map((kundli) => (
                                <div
                                    key={kundli.id}
                                    onClick={() => onSelect(kundli, true)}
                                    className={`group flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors ${selectedId === kundli.id
                                        ? 'bg-primary-600/20 border border-primary-500/30'
                                        : 'bg-white/5 hover:bg-white/10 border border-transparent'
                                        }`}
                                >
                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-white font-bold text-sm shrink-0">
                                        {getInitial(kundli.name)}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="font-medium text-white text-sm truncate">{kundli.name}</span>
                                            <span className="text-[10px] text-primary-300/50 uppercase">{kundli.gender?.charAt(0)}</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-[11px] text-primary-200/50">
                                            <Calendar size={10} />
                                            {formatDate(kundli.dob)}
                                        </div>
                                        <div className="flex items-center gap-2 text-[11px] text-primary-200/40 truncate">
                                            <MapPin size={10} />
                                            {kundli.birth_location || kundli.location}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <Cloud size={14} className="text-primary-400" />
                                        <div className="relative group/tooltip">
                                            <button
                                                onClick={(e) => handleDeleteCloud(kundli.id, e)}
                                                className="p-1.5 rounded-lg hover:bg-red-500/20 text-red-400 transition-colors"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                            <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-cosmic-900 border border-white/20 text-white text-[10px] rounded pointer-events-none opacity-0 group-hover/tooltip:opacity-100 transition-opacity whitespace-nowrap z-50">
                                                {t.deleteFromCloud}
                                            </span>
                                        </div>
                                        <ChevronRight size={14} className="text-primary-300/30" />
                                    </div>
                                </div>
                            ))}
                        
                    </>
                )}

                {/* Local Kundlis */}
                {filteredLocal.length > 0 && (
                    <>
                        <div className="flex items-center gap-2 text-xs text-primary-300/60 font-medium uppercase tracking-wider py-2 mt-4">
                            <HardDrive size={12} />
                            {t.savedLocally}
                        </div>
                        
                            {filteredLocal.map((kundli) => (
                                <div
                                    key={kundli.id}
                                    onClick={() => onSelect(kundli, false)}
                                    className={`group flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors ${selectedId === kundli.id
                                        ? 'bg-primary-600/20 border border-primary-500/30'
                                        : 'bg-white/5 hover:bg-white/10 border border-transparent'
                                        }`}
                                >
                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center text-white font-bold text-sm shrink-0">
                                        {getInitial(kundli.name)}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="font-medium text-white text-sm truncate">{kundli.name}</span>
                                            <span className="text-[10px] text-primary-300/50 uppercase">{kundli.gender?.charAt(0)}</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-[11px] text-primary-200/50">
                                            <Calendar size={10} />
                                            {formatDate(kundli.dob)}
                                        </div>
                                        <div className="flex items-center gap-2 text-[11px] text-primary-200/40 truncate">
                                            <MapPin size={10} />
                                            {kundli.location}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <HardDrive size={14} className="text-amber-400" />
                                        {isAuthenticated && (
                                            <div className="relative group/tooltip">
                                                <button
                                                    onClick={(e) => handleUploadToCloud(kundli, e)}
                                                    disabled={uploadingId === kundli.id}
                                                    className="p-1.5 rounded-lg hover:bg-primary-500/20 text-primary-400 transition-colors disabled:opacity-50"
                                                >
                                                    {uploadingId === kundli.id ? (
                                                        <div className="w-3.5 h-3.5 border-2 border-primary-400/30 border-t-primary-400 rounded-full animate-spin" />
                                                    ) : (
                                                        <Upload size={14} />
                                                    )}
                                                </button>
                                                <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-cosmic-900 border border-white/20 text-white text-[10px] rounded pointer-events-none opacity-0 group-hover/tooltip:opacity-100 transition-opacity whitespace-nowrap z-50">
                                                    {t.saveToCloud}
                                                </span>
                                            </div>
                                        )}
                                        <div className="relative group/tooltip">
                                            <button
                                                onClick={(e) => handleDeleteLocal(kundli.id, e)}
                                                className="p-1.5 rounded-lg hover:bg-red-500/20 text-red-400 transition-colors"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                            <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-cosmic-900 border border-white/20 text-white text-[10px] rounded pointer-events-none opacity-0 group-hover/tooltip:opacity-100 transition-opacity whitespace-nowrap z-50">
                                                {t.removeLocal}
                                            </span>
                                        </div>
                                        <ChevronRight size={14} className="text-primary-300/30" />
                                    </div>
                                </div>
                            ))}
                        
                    </>
                )}
            </div>

            {/* Login prompt for guests */}
            {!isAuthenticated && filteredLocal.length > 0 && (
                <div className="mt-4 p-3 rounded-xl bg-primary-600/10 border border-primary-500/20 text-xs text-primary-200/70">
                    <Cloud size={14} className="inline mr-1.5" />
                    <span className="font-medium text-primary-300">{t.login}</span> {t.toSyncKundlis}
                </div>
            )}
        </div>
    );
};

export default SavedKundliList;
